import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Loader2, Globe } from 'lucide-react';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { DebateArgument } from '@/hooks/useStrategicStream';

export function DebatePanelShowcase({ debateArgs, isActive, isSmallScreen }: {
  debateArgs: DebateArgument[]; isActive: boolean; isSmallScreen: boolean;
}) {
  const [expandedArgs, setExpandedArgs] = useState<Set<number>>(new Set());
  const sortedArgs = [...debateArgs].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  const debaterNames = Array.from(new Set(debateArgs.map(a => a.debaterName)));

  const toggleArg = (idx: number) => {
    setExpandedArgs(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {/* Debate position summary header */}
      {debaterNames.length >= 2 && sortedArgs.length > 0 && (
        <div className={cn('flex items-center justify-center gap-3 py-2 px-3 rounded-lg bg-surface-light/50 border border-border/30', isSmallScreen ? 'text-xs' : 'text-sm')}>
          {debaterNames.slice(0, 3).map((name, i) => {
            const firstArg = sortedArgs.find(a => a.debaterName === name);
            const colorScheme = i % 2 === 0
              ? { text: 'text-blue-400', badge: 'bg-blue-500/30' }
              : { text: 'text-orange-400', badge: 'bg-orange-500/30' };
            if (i === 2) {
              return (
                <span key={name} className="flex items-center gap-1.5">
                  <span className="text-text-muted">|</span>
                  <span className={cn('px-2 py-0.5 rounded text-xs font-medium', 'bg-purple-500/30 text-purple-400')}>
                    {name}: {firstArg?.position || 'analyzing'}
                    {firstArg?.confidence != null && ` ${Math.round(firstArg.confidence * 100)}%`}
                  </span>
                </span>
              );
            }
            return (
              <span key={name} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-text-muted">vs</span>}
                <span className={cn('px-2 py-0.5 rounded text-xs font-medium', colorScheme.badge, colorScheme.text)}>
                  {name}: {firstArg?.position || 'analyzing'}
                  {firstArg?.confidence != null && ` ${Math.round(firstArg.confidence * 100)}%`}
                </span>
              </span>
            );
          })}
        </div>
      )}

      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {sortedArgs.map((arg, idx) => {
          const debaterIndex = debaterNames.indexOf(arg.debaterName);
          const isLeftAligned = debaterIndex % 2 === 0;
          const colorScheme = debaterIndex === 2
            ? { bg: 'from-purple-500/15 to-purple-600/8', border: 'border-purple-500/30', text: 'text-purple-400', badge: 'bg-purple-500/30' }
            : isLeftAligned
            ? { bg: 'from-blue-500/15 to-blue-600/8', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/30' }
            : { bg: 'from-orange-500/15 to-orange-600/8', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/30' };

          const isLong = (arg.argument || '').length > 600;
          const isArgExpanded = expandedArgs.has(idx);

          return (
            <div
              key={idx}
              className={cn('flex', isLeftAligned ? 'justify-start' : 'justify-end')}
            >
              <div
                className={cn(
                  'p-4 rounded-xl border transition-all duration-300',
                  'backdrop-blur-sm bg-gradient-to-br',
                  colorScheme.bg,
                  colorScheme.border,
                  isLeftAligned ? 'rounded-tl-sm' : 'rounded-tr-sm',
                  isSmallScreen ? 'max-w-[95%]' : 'max-w-[85%]'
                )}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={cn(
                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold',
                    colorScheme.badge,
                    colorScheme.text
                  )}>
                    {debaterIndex === 0 ? 'A' : debaterIndex === 1 ? 'B' : 'C'}
                  </div>
                  <span className={cn('text-sm font-mono font-medium', colorScheme.text)}>
                    {arg.debaterName || arg.debaterRole || 'Debater'}
                  </span>
                  <span className="text-xs text-text-muted font-mono">Round {arg.roundNumber || 1}</span>
                  {arg.position && (
                    <span className={cn('ml-auto px-2 py-0.5 rounded text-xs uppercase',
                      arg.position === 'support' ? 'bg-green-500/20 text-green-400' :
                      arg.position === 'oppose' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400')}>
                      {arg.position}
                    </span>
                  )}
                </div>
                {/* Argument text with truncation for long arguments */}
                <div className="relative">
                  <div className={cn(
                    'text-text-primary leading-relaxed',
                    isSmallScreen ? 'text-sm' : 'text-base',
                    isLong && !isArgExpanded && 'max-h-[200px] overflow-hidden'
                  )}>
                    <MarkdownContent content={arg.argument || 'Forming argument...'} className={isSmallScreen ? 'text-sm' : 'text-base'} />
                  </div>
                  {isLong && !isArgExpanded && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-surface/90 to-transparent pointer-events-none" />
                  )}
                </div>
                {isLong && (
                  <button
                    onClick={() => toggleArg(idx)}
                    className={cn('mt-1 text-xs hover:underline', colorScheme.text)}
                  >
                    {isArgExpanded ? 'Show less' : 'Read full argument'}
                  </button>
                )}
                {arg.evidenceCited && arg.evidenceCited.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border-subtle">
                    <div className="text-xs text-text-secondary uppercase tracking-wide mb-1.5 flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      Evidence Cited
                    </div>
                    <ul className="text-xs text-text-secondary space-y-1">
                      {arg.evidenceCited.map((e, j) => (
                        <li key={j} className="leading-relaxed">
                          <MarkdownContent content={e} className="text-xs inline [&_p]:inline [&_a]:text-amber-400 [&_a]:hover:text-amber-300" inline />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {isActive && debateArgs.length === 0 && (
          <div className="text-center text-text-muted py-4 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Debate starting...
          </div>
        )}
      </div>
    </div>
  );
}
